import { waitForOptimizelySdk, OPTIMIZELY_MAX_WAIT_MS } from '~/utils/optimizely/sdk-ready'

/**
 * HI-1430 — the Energy single-question-per-page funnel A/B test.
 *
 * 0 = control (`/energy/quote/form/step1..step8`), 1 = the SQPP variant
 * (`/energy/quote/form/<named-slug>`).
 *
 * Four surfaces carry the assignment:
 *   - `window.ABTestEnergySqppFunnel` — set by the Optimizely variation's JS
 *   - `abtest.energySqppFunnel` in session AND local storage
 *   - the `abtest_energySqppFunnel` cookie — the only *server-readable* copy,
 *     which is what lets the global middleware redirect `step1` before paint
 *   - `?sqpp=0|1` — a force param so QA never depends on the dashboard
 */

export const SQPP_WINDOW_KEY = 'ABTestEnergySqppFunnel'
export const SQPP_STORAGE_KEY = 'abtest.energySqppFunnel'
export const SQPP_COOKIE_KEY = 'abtest_energySqppFunnel'
export const SQPP_FORCE_PARAM = 'sqpp'

// Matches the control's own grace window on details.vue: a ready SDK does not
// mean the variation's custom JS has assigned our global yet.
export const SQPP_FLAG_GRACE_MS = 300

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30

declare global {
  interface Window {
    ABTestEnergySqppFunnel?: number | string | null
  }
}

function toVariation (value: unknown): 0 | 1 {
  return Number(value) === 1 ? 1 : 0
}

function isSet (value: unknown): boolean {
  return value !== null && value !== undefined && value !== ''
}

export function useEnergySqppCookie () {
  return useCookie<string | null>(SQPP_COOKIE_KEY, {
    maxAge: COOKIE_MAX_AGE_SECONDS,
    path: '/',
    sameSite: 'lax'
  })
}

/**
 * Read the assignment.
 *
 * Precedence: session -> window -> localStorage -> cookie -> 0.
 *
 * Session first, NOT localStorage first. The house pattern documented in
 * `docs/optimizely-ab-tests.md` reads localStorage first, and that is exactly
 * the bug live at `[step].vue:443-445`: it pins a visitor to whichever arm they
 * saw first, so an Optimizely *control* preview link keeps rendering the
 * variant. HI-1318 already fixed this for the lead-gate flag
 * (`details.lead-gate-consistency.test.ts` is its regression test); this
 * follows that precedent and the deviation is called out in the PR.
 */
export function readEnergySqppAssignment (): 0 | 1 {
  if (!import.meta.client) {
    return 0
  }

  const sessionValue = getSession(SQPP_STORAGE_KEY)
  if (isSet(sessionValue)) {
    return toVariation(sessionValue)
  }

  const windowValue = window[SQPP_WINDOW_KEY]
  if (isSet(windowValue)) {
    return toVariation(windowValue)
  }

  const localValue = getLocal(SQPP_STORAGE_KEY)
  if (isSet(localValue)) {
    return toVariation(localValue)
  }

  return 0
}

/**
 * Persist a REAL assignment across session, local and cookie.
 *
 * Only ever called with a value Optimizely actually handed us. Writing a
 * speculative 0 when the global is absent would pin variant visitors to the
 * control on every page the experiment does not target.
 */
export function captureEnergySqppAssignment (): 0 | 1 | null {
  if (!import.meta.client) {
    return null
  }

  const windowValue = window[SQPP_WINDOW_KEY]

  if (!isSet(windowValue)) {
    return null
  }

  const variation = toVariation(windowValue)

  setSession(SQPP_STORAGE_KEY, variation)
  setLocal(SQPP_STORAGE_KEY, variation)
  useEnergySqppCookie().value = String(variation)

  return variation
}

/** Clear every copy — used when the SDK says the visitor is explicitly control. */
export function clearEnergySqppAssignment (): void {
  if (!import.meta.client) {
    return
  }

  removeSession(SQPP_STORAGE_KEY)
  removeLocal(SQPP_STORAGE_KEY)
  useEnergySqppCookie().value = null
}

/**
 * `?sqpp=0|1`. Returns null when the param is absent or not 0/1, so an
 * unrelated query value can never be read as an assignment.
 */
export function readSqppForceParam (query: Record<string, unknown> = {}): 0 | 1 | null {
  const raw = query?.[SQPP_FORCE_PARAM]
  const value = Array.isArray(raw) ? raw[0] : raw

  if (value === '1' || value === 1) {
    return 1
  }

  if (value === '0' || value === 0) {
    return 0
  }

  return null
}

/**
 * Wait for the SDK, then give the variation's custom JS a short grace window to
 * assign the global.
 *
 * The variant page never blocks paint on this — being on a slug URL already
 * proves the decision was made upstream, so re-introducing the wait HI-1494
 * deliberately removed would be a regression. It runs fire-and-forget, purely
 * so `captureEnergySqppAssignment` and the preview reconciliation below have
 * something to read.
 */
export async function waitForEnergySqppFlag (
  maxWaitMs = OPTIMIZELY_MAX_WAIT_MS,
  graceMs = SQPP_FLAG_GRACE_MS
): Promise<void> {
  if (!import.meta.client) {
    return
  }

  if (isSet(window[SQPP_WINDOW_KEY])) {
    return
  }

  await waitForOptimizelySdk(maxWaitMs)

  const graceStart = Date.now()
  while (!isSet(window[SQPP_WINDOW_KEY]) && Date.now() - graceStart < graceMs) {
    // setTimeout, not rAF — rAF is paused in hidden tabs.
    // eslint-disable-next-line no-await-in-loop
    await new Promise(resolve => setTimeout(resolve, 50))
  }
}

/**
 * After the wait, is Optimizely explicitly telling us this visitor is control?
 *
 * Only an explicit 0 counts. An absent global means "not bucketed / not
 * resolved", which must never demote someone already on a variant URL.
 */
export function isExplicitlyControl (): boolean {
  if (!import.meta.client) {
    return false
  }

  const windowValue = window[SQPP_WINDOW_KEY]

  return isSet(windowValue) && toVariation(windowValue) === 0
}
